import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { getStaffQuarterAllFlats, getStaffQuarterPriorityList, generateStaffQuarterWillingness, getStaffQuarterQuarterTypes, getStaffQuarterPools, getStaffQuarterLocations, updateFlatPoolType } from "../../../../core/data/redux/staffQuarterSlice";
import { Link, useSearchParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Spin, Tag, Badge, Card, Empty, Breadcrumb, Popover, Modal, Table, Button, Select, Input } from "antd";
import { toast } from "react-toastify";
import EditFlatModal from "./EditFlatModal";
import { formatFlatNoWithBlock, padFlatNumber } from "../staffQuarterUtils";

const LocationFlatView = () => {
    const routes = all_routes;
    const [searchParams] = useSearchParams();
    const locationName = searchParams.get("location") || "";
    const locationCode = searchParams.get("code") || "";

    const dispatch = useDispatch<any>();
    const [towers, setTowers] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [isWillingnessModalVisible, setIsWillingnessModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    const [selectedFlat, setSelectedFlat] = useState<any>(null);
    const [priorityList, setPriorityList] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isPriorityLoading, setIsPriorityLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [customPageSize, setCustomPageSize] = useState<number>(10);
    const [empNoSearch, setEmpNoSearch] = useState<string>("");
    const [skipRemark, setSkipRemark] = useState<string>("");
    const [skipRemarkError, setSkipRemarkError] = useState<string>("");

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedFlatRecord, setSelectedFlatRecord] = useState<any>(null);

    const [selectedType, setSelectedType] = useState<string>("");
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedPoolTypeId, setSelectedPoolTypeId] = useState<string | number>("");
    const [quarterTypes, setQuarterTypes] = useState<any[]>([]);
    const [poolTypes, setPoolTypes] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);

    useEffect(() => {
        dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
            if (res.payload?.success) {
                setQuarterTypes(res.payload.data || []);
            }
        });
        dispatch(getStaffQuarterPools({})).then((res: any) => {
            if (res.payload?.success) {
                setPoolTypes(res.payload.data || []);
            }
        });
        dispatch(getStaffQuarterLocations({})).then((res: any) => {
            if (res.payload?.success) {
                setLocations(res.payload.data || []);
            }
        });
    }, [dispatch]);

    const fetchTowers = (typeOverride?: string, statusOverride?: string, poolTypeOverride?: string | number) => {
        setIsLoading(true);
        const typeToUse = typeOverride !== undefined ? typeOverride : selectedType;
        const statusToUse = statusOverride !== undefined ? statusOverride : selectedStatus;
        const poolTypeToUse = poolTypeOverride !== undefined ? poolTypeOverride : selectedPoolTypeId;

        const payload: any = {
            Header: { device_type: "WEB" },
            location: locationCode,
            limit: 1000,
            page: 1
        };

        // Sometimes type comes as "Type-IV", backend might expect "IV" or exact string.
        // Assuming backend expects exact name like FlatMaster
        if (typeToUse) payload.flatType = typeToUse;
        if (statusToUse) payload.status = statusToUse;
        if (poolTypeToUse) payload.poolTypeId = poolTypeToUse;

        dispatch(getStaffQuarterAllFlats(payload)).then((res: any) => {
            if (res.payload?.success) {
                let apiData = [];
                if (Array.isArray(res.payload.data)) {
                    apiData = res.payload.data;
                } else if (res.payload.data && Array.isArray(res.payload.data.data)) {
                    apiData = res.payload.data.data;
                } else if (res.payload.data && Array.isArray(res.payload.data.flats)) {
                    apiData = res.payload.data.flats;
                } else if (res.payload.data && Array.isArray(res.payload.data.content)) {
                    apiData = res.payload.data.content;
                }

                // Perform client-side filtering for pool type
                if (poolTypeToUse) {
                    apiData = apiData.filter((item: any) =>
                        String(item.poolTypeId) === String(poolTypeToUse) ||
                        String(item.poolId) === String(poolTypeToUse) ||
                        String(item.pool_type_id) === String(poolTypeToUse)
                    );
                }

                const blocks: Record<string, {
                    seenFlatIds: Set<string>;
                    quarterMap: Record<string, any[]>;
                    floorsMap: Record<number, any[]>;
                }> = {};

                const resolveQuarterType = (item: any): string => {
                    const rawType = item.quarterType || item.quarter_type || item.quarterTypeName || item.flatType || item.category || item.type;
                    if (rawType && typeof rawType === 'string' && rawType.trim().length > 0) {
                        return rawType.trim();
                    }
                    if (rawType && typeof rawType === 'object' && rawType.name) {
                        return String(rawType.name).trim();
                    }
                    return "Unspecified Quarter Type";
                };

                const naturalSortQuarterTypes = (a: string, b: string): number => {
                    if (a === "Unspecified Quarter Type") return 1;
                    if (b === "Unspecified Quarter Type") return -1;
                    const romanMap: Record<string, number> = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10 };
                    const getSortWeight = (str: string): number => {
                        const match = str.match(/(?:type|quarter)?\s*[-_]?\s*([ivx0-9]+)/i);
                        if (match) {
                            const token = match[1].toLowerCase();
                            if (romanMap[token]) return romanMap[token];
                            const num = parseInt(token, 10);
                            if (!isNaN(num)) return num;
                        }
                        return 99;
                    };
                    const weightA = getSortWeight(a);
                    const weightB = getSortWeight(b);
                    if (weightA !== weightB) return weightA - weightB;
                    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
                };

                apiData.forEach((item: any) => {
                    const rawBlock = item.block || item.blockName || item.tower || "";
                    const blockName = "Tower " + (rawBlock ? String(rawBlock).trim() : "Unknown");
                    const flatUniqueKey = `${rawBlock}-${item.flatNo || item.id || item.flat_id}`;

                    if (!blocks[blockName]) {
                        blocks[blockName] = {
                            seenFlatIds: new Set<string>(),
                            quarterMap: {},
                            floorsMap: {}
                        };
                    }

                    if (blocks[blockName].seenFlatIds.has(flatUniqueKey)) {
                        return; // Prevent duplicate flat cards
                    }
                    blocks[blockName].seenFlatIds.add(flatUniqueKey);

                    let statusStr = "Occupied";
                    if (item.status === "VACANT") statusStr = "Vacant";
                    else if (item.status === "OCCUPIED") statusStr = "Occupied";
                    else if (item.status === "VACANT_INITIATED" || item.status === "VACANT INITIATED" || item.status === "INITIATED") statusStr = "Vacant Initiated";
                    else statusStr = item.status;

                    const qType = resolveQuarterType(item);
                    const flatNoStr = (item.flatNo || "").toString();
                    const levelStr = flatNoStr.length > 2 ? flatNoStr.slice(0, -2) : "0";
                    const level = parseInt(levelStr, 10) || 0;

                    const flatObj = {
                        id: flatUniqueKey,
                        flatId: item,
                        flatNo: item.flatNo,
                        status: statusStr,
                        quarterType: qType,
                        pool: item.pool || "",
                        category: item.category || "",
                        resident: item.pool || "",
                        days: 0,
                        itemData: item
                    };

                    if (!blocks[blockName].quarterMap[qType]) {
                        blocks[blockName].quarterMap[qType] = [];
                    }
                    blocks[blockName].quarterMap[qType].push(flatObj);

                    if (!blocks[blockName].floorsMap[level]) {
                        blocks[blockName].floorsMap[level] = [];
                    }
                    blocks[blockName].floorsMap[level].push(flatObj);
                });

                const formattedTowers = Object.keys(blocks).sort().map(towerName => {
                    const blockData = blocks[towerName];
                    const quarterGroups = Object.keys(blockData.quarterMap)
                        .sort(naturalSortQuarterTypes)
                        .map(qType => ({
                            quarterType: qType,
                            flats: blockData.quarterMap[qType].sort((a: any, b: any) => {
                                const numA = parseInt(String(a.flatNo).replace(/\D/g, ''), 10) || 0;
                                const numB = parseInt(String(b.flatNo).replace(/\D/g, ''), 10) || 0;
                                return numA - numB;
                            })
                        }));

                    const totalFlatsCount = quarterGroups.reduce((acc, qg) => acc + qg.flats.length, 0);
                    const blockLetter = towerName.replace("Tower ", "").trim();
                    const displayBlockName = blockLetter.length === 1 ? `Block ${blockLetter}` : towerName;

                    const floors = Object.keys(blockData.floorsMap)
                        .sort((a, b) => parseInt(b) - parseInt(a))
                        .map(level => ({
                            level: parseInt(level),
                            flats: blockData.floorsMap[parseInt(level)].sort((a: any, b: any) => Number(a.flatNo) - Number(b.flatNo))
                        }));

                    return {
                        name: towerName,
                        blockLetter,
                        displayBlockName,
                        totalFlatsCount,
                        quarterGroups,
                        floors
                    };
                });

                setTowers(formattedTowers);
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchTowers();
    }, [dispatch, locationName, locationCode]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Occupied":
                return "#28a745"; // Green
            case "Vacant":
                return "#E30613"; // Red
            case "Vacant Initiated":
                return "#007bff"; // Blue
            default:
                return "#64748b";
        }
    };

    const [selectedFlats, setSelectedFlats] = useState<any[]>([]);

    const firstSelectedFlat = selectedFlats[0] || null;
    const getFlatQuarterType = (flat: any) => flat?.quarterType || flat?.itemData?.quarterType || flat?.itemData?.flatType || flat?.itemData?.quarter_type || "N/A";
    const getFlatPool = (flat: any) => flat?.pool || flat?.itemData?.pool || flat?.itemData?.poolName || "N/A";
    const getFlatKey = (flat: any) => flat?.id || `${flat?.itemData?.block || flat?.block}-${flat?.flatNo}`;

    const allowedQuarterType = firstSelectedFlat ? getFlatQuarterType(firstSelectedFlat) : null;
    const allowedPool = firstSelectedFlat ? getFlatPool(firstSelectedFlat) : null;

    const isFlatSelected = (flat: any) => {
        const key = getFlatKey(flat);
        return selectedFlats.some(f => getFlatKey(f) === key);
    };

    const checkFlatEligibility = (flat: any): { isEligible: boolean; reason?: string } => {
        if (flat.status !== "Vacant") {
            return { isEligible: false, reason: `Flat ${flat.flatNo} is ${flat.status}. Only Vacant flats can be selected.` };
        }
        if (selectedFlats.length > 0 && !isFlatSelected(flat)) {
            const flatQType = getFlatQuarterType(flat);
            const flatPool = getFlatPool(flat);

            if (allowedQuarterType && flatQType !== allowedQuarterType) {
                return {
                    isEligible: false,
                    reason: `Cannot select Flat ${flat.flatNo}: Quarter type (${flatQType}) does not match allowed quarter type (${allowedQuarterType}).`
                };
            }
            if (allowedPool && flatPool !== allowedPool) {
                return {
                    isEligible: false,
                    reason: `Cannot select Flat ${flat.flatNo}: Pool (${flatPool}) does not match allowed pool (${allowedPool}).`
                };
            }
        }
        return { isEligible: true };
    };

    const handleToggleSelectFlat = (flat: any, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const selected = isFlatSelected(flat);
        if (selected) {
            const updated = selectedFlats.filter(f => getFlatKey(f) !== getFlatKey(flat));
            setSelectedFlats(updated);
        } else {
            const check = checkFlatEligibility(flat);
            if (!check.isEligible) {
                toast.warning(check.reason);
                return;
            }
            setSelectedFlats(prev => [...prev, flat]);
        }
    };

    const handleWillingnessClick = (flat: any) => {
        const check = checkFlatEligibility(flat);
        if (!check.isEligible) {
            toast.warning(check.reason);
            return;
        }
        let updatedFlats = selectedFlats;
        if (!isFlatSelected(flat)) {
            if (selectedFlats.length === 0) {
                updatedFlats = [flat];
                setSelectedFlats([flat]);
            } else {
                updatedFlats = [...selectedFlats, flat];
                setSelectedFlats(prev => [...prev, flat]);
            }
        }
        const primaryFlat = updatedFlats[0] || flat;
        setSelectedFlat(primaryFlat);
        setIsWillingnessModalVisible(true);
        fetchPriorityList(primaryFlat, customPageSize || 10);
    };

    const handleOpenMultiWillingnessModal = () => {
        if (selectedFlats.length === 0) {
            toast.warning("Please select at least one vacant flat.");
            return;
        }
        const primaryFlat = selectedFlats[0];
        setSelectedFlat(primaryFlat);
        setIsWillingnessModalVisible(true);
        fetchPriorityList(primaryFlat, customPageSize || 10);
    };

    const { isSkippingHigherPriority, skippedEmployees } = useMemo(() => {
        if (!selectedRowKeys || selectedRowKeys.length === 0 || !priorityList || priorityList.length === 0) {
            return { isSkippingHigherPriority: false, skippedEmployees: [] };
        }

        const selectedKeySet = new Set(selectedRowKeys.map(k => String(k)));

        const selectedIndices: number[] = [];
        priorityList.forEach((item: any, idx: number) => {
            const itemKey = String(item.referenceNumber || item.registrationId || item.id || item.key || `key-${idx}`);
            if (selectedKeySet.has(itemKey)) {
                selectedIndices.push(idx);
            }
        });

        if (selectedIndices.length === 0) {
            return { isSkippingHigherPriority: false, skippedEmployees: [] };
        }

        const maxSelectedIndex = Math.max(...selectedIndices);
        const skipped: any[] = [];

        for (let i = 0; i < maxSelectedIndex; i++) {
            if (!selectedIndices.includes(i)) {
                skipped.push(priorityList[i]);
            }
        }

        return {
            isSkippingHigherPriority: skipped.length > 0,
            skippedEmployees: skipped
        };
    }, [selectedRowKeys, priorityList]);

    const fetchPriorityList = (flat: any, limit = 100) => {
        setEmpNoSearch("");
        setSkipRemark("");
        setSkipRemarkError("");
        setIsPriorityLoading(true);
        const matchedLocation = locations.find(l =>
            String(l.code).toLowerCase() === String(locationCode).toLowerCase() ||
            String(l.id) === String(locationCode)
        );
        const locationIdVal = matchedLocation ? matchedLocation.id : (isNaN(Number(locationCode)) ? null : Number(locationCode));

        dispatch(getStaffQuarterPriorityList({
            locationId: locationIdVal,
            quarter_type_code: flat.itemData?.quarterTypeId || null,
            quarterTypeCode: flat.itemData?.quarterTypeId || null,
            quarterType: flat.itemData?.flatType || flat.quarterType || null,
            category: flat.itemData?.category || flat.itemData?.category_name || flat.itemData?.flat_category || flat.category || flat.flat_category || flat.categoryName || null,
            poolTypeId: flat.itemData?.poolTypeId || null,
            page: 1,
            limit: limit
        })).then((res: any) => {
            setIsPriorityLoading(false);
            if (res.payload?.success) {
                let data = [];
                if (Array.isArray(res.payload.data)) data = res.payload.data;
                else if (res.payload.data?.list) data = res.payload.data.list;
                else if (res.payload.data?.data) data = res.payload.data.data;
                else if (res.payload.data?.content) data = res.payload.data.content;

                // Filter out candidates who already have approved willingness or willingness generated for this location
                const listWithKeys = data
                    .filter((item: any) => {
                        if (item.isWillingnessApproved === true || item.isWillingnessApproved === "true" || item.isWillingnessApproved === 1) {
                            return false;
                        }
                        let isGeneratedForThisLoc = false;
                        if (locationIdVal) {
                            const numericLocId = Number(locationIdVal);
                            if (item.locationPreference1Id === numericLocId) {
                                isGeneratedForThisLoc = !!(item.location1IsWillingnessGenerated || item.isWillingnessGeneratedForLoc1);
                            } else if (item.locationPreference2Id === numericLocId) {
                                isGeneratedForThisLoc = !!(item.location2IsWillingnessGenerated || item.isWillingnessGeneratedForLoc2);
                            } else if (item.locationPreference3Id === numericLocId) {
                                isGeneratedForThisLoc = !!(item.location3IsWillingnessGenerated || item.isWillingnessGeneratedForLoc3);
                            }
                        } else {
                            isGeneratedForThisLoc = !!(
                                item.willingnessReferenceNumber ||
                                item.location1WillingnessReferenceNumber ||
                                item.location2WillingnessReferenceNumber ||
                                item.location3WillingnessReferenceNumber
                            );
                        }
                        return !isGeneratedForThisLoc;
                    })
                    .map((item: any, idx: number) => ({
                        ...item,
                        priority: `#${idx + 1}`,
                        key: item.referenceNumber || item.registrationId || item.id || `key-${idx}`,
                    }));
                setPriorityList(listWithKeys);

                const defaultSelectedKeys = listWithKeys.slice(0, 10).map((item: any) => item.key);
                setSelectedRowKeys(defaultSelectedKeys);
            } else {
                const errorMsg = typeof res.payload === 'string' ? res.payload : (res.payload?.data?.message || res.payload?.message || "Failed to fetch priority list");
                toast.error(errorMsg);
                setPriorityList([]);
                setSelectedRowKeys([]);
            }
        });
    };

    const handleGenerateWillingness = () => {
        if (selectedRowKeys.length === 0) {
            toast.warning("Please select at least one priority record.");
            return;
        }

        const flatsToSubmit = selectedFlats.length > 0 ? selectedFlats : (selectedFlat ? [selectedFlat] : []);
        if (flatsToSubmit.length === 0) {
            toast.warning("No flat selected for willingness generation.");
            return;
        }

        const flatIdsList = flatsToSubmit.map((f: any) =>
            f.itemData?.flatId || f.itemData?.flat_id || f.flatId?.flatId || f.flatId || f.id
        ).filter(Boolean);

        const uniqueFlatIds = Array.from(new Set(flatIdsList));
        if (uniqueFlatIds.length !== flatIdsList.length) {
            toast.error("Duplicate flat IDs detected in selection.");
            return;
        }

        if (isSkippingHigherPriority) {
            if (!skipRemark || !skipRemark.trim()) {
                setSkipRemarkError("Reason for skipping higher-priority employee(s) is mandatory.");
                toast.error("Please enter a reason for skipping higher-priority employee(s).");
                return;
            }
        }

        const remarkValue = isSkippingHigherPriority ? skipRemark.trim() : "";

        const payload = {
            registrationIds: selectedRowKeys,
            flatIds: uniqueFlatIds,
            flatId: uniqueFlatIds[0],
            locationCode: locationCode || flatsToSubmit[0]?.itemData?.location || flatsToSubmit[0]?.itemData?.locationName || "",
            quarterType: allowedQuarterType || getFlatQuarterType(flatsToSubmit[0]),
            pool: allowedPool || getFlatPool(flatsToSubmit[0]),
            priorityRemarks: remarkValue,
            priority_remarks: remarkValue,
            skipRemark: remarkValue,
            skipReason: remarkValue,
            skippingReason: remarkValue,
            skip_remark: remarkValue,
            skip_reason: remarkValue,
            reasonForSkipping: remarkValue,
            remarks: remarkValue,
            remark: remarkValue
        };

        setIsGenerating(true);
        dispatch(generateStaffQuarterWillingness(payload)).then((res: any) => {
            setIsGenerating(false);
            if (res.payload?.success || res.payload?.message?.toLowerCase().includes("success")) {
                toast.success(`Willingness generated successfully for ${flatsToSubmit.length} flat(s)!`);
                setIsWillingnessModalVisible(false);
                setSelectedFlats([]);
                setSelectedFlat(null);
                setSkipRemark("");
                setSkipRemarkError("");
                fetchTowers();
            } else {
                const errorMsg = typeof res.payload === 'string' ? res.payload : (res.payload?.data?.message || res.payload?.message || "Failed to generate willingness");
                toast.error(errorMsg);
            }
        });
    };

    const filteredPriorityList = priorityList.filter((item: any) => {
        if (!empNoSearch.trim()) return true;
        const query = empNoSearch.trim().toLowerCase();
        const empNo = String(item.employeeNumber || item.employeeNo || item.empNo || item.emp_no || item.employee_number || "").toLowerCase();
        const name = String(item.employeeName || item.name || "").toLowerCase();
        const regId = String(item.id || item.registrationId || item.referenceNumber || "").toLowerCase();
        return empNo.includes(query) || name.includes(query) || regId.includes(query);
    });

    const priorityColumns = [
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            render: (text: string, _record: any, index: number) => <Tag color="blue">{text || `#${index + 1}`}</Tag>
        },
        {
            title: "Reg. ID",
            dataIndex: "id",
            key: "id",
            render: (text: string, record: any) => text || record.referenceNumber || record.registrationId || 'N/A'
        },
        {
            title: "Emp No",
            dataIndex: "employeeNumber",
            key: "employeeNumber",
            render: (text: string, record: any) => text || record.employeeNo || record.empNo || 'N/A'
        },
        {
            title: "Name",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (text: string, record: any) => text || record.name || 'N/A'
        },
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation"
        },
        {
            title: "Priority Date",
            dataIndex: "registrationTimestamp",
            key: "registrationTimestamp",
            render: (text: string, record: any) => {
                const dateVal = text || record.priorityDate || record.registrationDate;
                return dateVal ? new Date(dateVal).toLocaleDateString() : 'N/A';
            }
        }
    ];

    const handleEditPoolClick = (flat: any) => {
        const flatData = flat.itemData || flat;
        setSelectedFlatRecord({
            flatId: flatData.flatId || flatData.flat_id || flatData.flatNumber || flatData.id,
            location: locationName || flatData.locationName || flatData.location || locationCode,
            block: flatData.block || flat.block || "",
            flatNo: flat.flatNo || flatData.flatNo || "",
            type: flatData.flatType || flat.quarterType || "",
            pool: flatData.pool || flat.pool || "",
            category: flatData.category || flat.category || "",
            poolTypeId: flatData.poolTypeId || flatData.pool_type_id || null
        });
        setShowEditModal(true);
    };

    const handleUpdateFlatPool = (flatId: string, poolTypeId: number | null, category?: string) => {
        return dispatch(updateFlatPoolType({
            flat_id: flatId,
            pool_type_id: poolTypeId,
            category: category,
            flat_category: category,
            category_name: category
        })).then((res: any) => {
            if (res.payload?.success || res.payload?.status === 200) {
                fetchTowers();
            }
            return res;
        });
    };

    const renderPopoverContent = (flat: any) => {
        const formattedFlatNo = formatFlatNoWithBlock(flat.itemData || flat, flat.itemData?.block || flat.block) || flat.itemData?.flatNumber || flat.itemData?.flatNo || flat.flatNo || 'N/A';

        return (
            <div style={{ padding: '4px', textAlign: 'left', minWidth: '180px' }}>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-2 pb-2 border-bottom border-secondary">
                    <span style={{ fontWeight: 700, fontSize: '14px', color: '#fff' }}>UNIT {formattedFlatNo}</span>
                    <Badge color={getStatusColor(flat.status)} count={flat.status} style={{ fontSize: '10px', boxShadow: 'none' }} />
                </div>
                <div className="d-flex flex-column gap-1">
                    <div className="d-flex justify-content-between">
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>FLAT NO:</span>
                        <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{formattedFlatNo}</span>
                    </div>
                <div className="d-flex justify-content-between">
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>TYPE:</span>
                    <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{flat.itemData?.flatType || flat.quarterType || 'N/A'}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>POOL:</span>
                    <div className="d-flex align-items-center gap-1">
                        <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{flat.itemData?.pool || flat.pool || 'N/A'}</span>
                        <button
                            type="button"
                            className="btn btn-link p-0 border-0 ms-1 d-inline-flex align-items-center"
                            style={{ fontSize: '12px', lineHeight: 1, color: '#38bdf8' }}
                            title="Edit Pool"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditPoolClick(flat);
                            }}
                        >
                            <i className="ti ti-edit"></i>
                        </button>
                    </div>
                </div>
                <div className="d-flex justify-content-between">
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>CATEGORY:</span>
                    <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{flat.itemData?.category || flat.category || 'N/A'}</span>
                </div>

                {flat.status === "Vacant" && (
                    <div style={{ fontSize: '10px', color: '#28a745', fontWeight: 500, marginTop: '2px' }}>
                        Ready for Allotment
                    </div>
                )}

                {flat.status === "Occupied" && (flat.itemData?.employeeNo || flat.itemData?.employeeName) && (
                    <div className="mt-2 pt-2 border-top border-secondary">
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px' }}>OCCUPIED BY</div>
                        <div className="d-flex justify-content-between">
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>EMP NO:</span>
                            <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{flat.itemData?.employeeNo || 'N/A'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>NAME:</span>
                            <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{flat.itemData?.employeeName || 'N/A'}</span>
                        </div>
                        {flat.itemData?.dateOfOccupation && (
                            <div className="d-flex justify-content-between">
                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>OCCUPATION DATE:</span>
                                <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>
                                    {new Date(flat.itemData.dateOfOccupation).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {(() => {
                // Extract willingness data from various possible backend structures
                const getWillingnessData = (itemData: any) => {
                    if (!itemData) return null;
                    const possibleKeys = ['willingnessDetails', 'registrationDetails', 'willingnessData', 'activeWillingness', 'registration'];
                    for (const key of possibleKeys) {
                        let val = itemData[key];
                        if (val) {
                            if (val.data?.data) val = val.data.data;
                            else if (val.success !== undefined && val.data) val = val.data;

                            if (val.willingnessDetails || val.referenceNumber || val.isWilling) {
                                return val;
                            }
                        }
                    }
                    if (itemData.willingnessDetails && itemData.referenceNumber) return itemData;
                    // If willingnessDetails exists but not wrapper, mock wrapper
                    if (itemData.willingnessDetails && !itemData.referenceNumber) return { willingnessDetails: itemData.willingnessDetails, employeeName: itemData.employeeName, empId: itemData.empId };
                    return null;
                };

                const wData = getWillingnessData(flat.itemData);
                if (!wData) return null;

                const wd = wData.willingnessDetails || wData; // in case wData is the willingnessDetails itself
                if (!wd || (!wd.isWilling && !wData.employeeName)) return null;

                return (
                    <div className="mt-3 pt-2 border-top border-secondary">
                        <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '6px', letterSpacing: '0.5px' }}>WILLINGNESS DETAILS</div>
                        <div className="d-flex flex-column gap-1">
                            {wData.employeeName && (
                                <div className="d-flex justify-content-between align-items-center">
                                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>EMP:</span>
                                    <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>
                                        {wData.employeeName} {wData.empId ? `(${wData.empId})` : ''}
                                    </span>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center">
                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>STATUS:</span>
                                <span style={{
                                    fontSize: '10px',
                                    color: wd.isWilling === 'YES' ? '#28a745' : wd.isWilling === 'NO' ? '#E30613' : '#ffc107',
                                    fontWeight: 700
                                }}>
                                    {wd.isWilling || 'PENDING'}
                                </span>
                            </div>
                            {(wd.remark || wd.remarks) && (
                                <div style={{ fontSize: '9px', color: '#cbd5e1', fontStyle: 'italic', marginTop: '2px', lineHeight: '1.3' }}>
                                    "{wd.remark || wd.remarks}"
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            <div className="d-flex gap-2 mt-3">
                {flat.status === "Vacant" && (
                    <>
                        <Button
                            type={isFlatSelected(flat) ? "default" : "dashed"}
                            size="small"
                            onClick={(e) => handleToggleSelectFlat(flat, e)}
                            style={{
                                flex: 1,
                                borderRadius: '4px',
                                fontWeight: 600,
                                fontSize: '11px',
                                borderColor: isFlatSelected(flat) ? '#ef4444' : '#38bdf8',
                                color: isFlatSelected(flat) ? '#ef4444' : '#38bdf8',
                                background: 'transparent'
                            }}
                        >
                            {isFlatSelected(flat) ? "Deselect" : "Select Flat"}
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => handleWillingnessClick(flat)}
                            style={{ flex: 1, borderRadius: '4px', fontWeight: 600, fontSize: '11px' }}
                        >
                            Willingness
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

    const [expandedTowers, setExpandedTowers] = useState<Record<string, boolean>>({});

    const toggleTowerExpand = (towerName: string) => {
        setExpandedTowers(prev => ({
            ...prev,
            [towerName]: !prev[towerName]
        }));
    };

    const toggleAllTowers = (expand: boolean) => {
        const nextState: Record<string, boolean> = {};
        towers.forEach(t => {
            nextState[t.name] = expand;
        });
        setExpandedTowers(nextState);
    };

    const isAllExpanded = towers.length > 0 && towers.every(t => expandedTowers[t.name]);

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Premium Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div>
                        <h3 className="page-title mb-1" style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a' }}>
                            {locationName} Inventory Map
                        </h3>
                        <Breadcrumb items={[
                            { title: <Link to={routes.staffQuarter.inventory.dashboard}>Inventory</Link> },
                            { title: `${locationName} Tower View` }
                        ]} />
                    </div>
                    <div className="d-flex gap-2 mt-md-0 mt-3">
                        <Tag color="red" style={{ padding: '4px 12px', borderRadius: '20px', fontWeight: 600 }}>{locationCode}</Tag>
                    </div>
                </div>

                {/* Standardized Legend & Actions */}
                <div className="card mb-4 border-0 shadow-soft" style={{ borderRadius: '12px' }}>
                    <div className="card-body py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-4 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: '12px', height: '12px', background: '#28a745', borderRadius: '3px' }}></div>
                                <span className="text-muted fw-bold" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>OCCUPIED (GREEN)</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: '12px', height: '12px', background: '#E30613', borderRadius: '3px' }}></div>
                                <span className="text-muted fw-bold" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>VACANT (RED)</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ width: '12px', height: '12px', background: '#007bff', borderRadius: '3px' }}></div>
                                <span className="text-muted fw-bold" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>INITIATED</span>
                            </div>
                            <div className="d-flex align-items-center gap-2 ps-2 border-start">
                                <div style={{
                                    width: '24px',
                                    height: '14px',
                                    background: '#28a745',
                                    borderTop: '3.5px solid #eab308',
                                    borderRadius: '3px',
                                    position: 'relative',
                                    boxShadow: '0 0 0 1.5px #eab308'
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        top: '0px',
                                        left: '1px',
                                        background: '#eab308',
                                        color: '#000',
                                        fontSize: '5.5px',
                                        fontWeight: 900,
                                        padding: '0 1px',
                                        borderRadius: '1px',
                                        lineHeight: '7px'
                                    }}>SC/ST</span>
                                </div>
                                <span className="text-muted fw-bold" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>SC/ST CATEGORY (YELLOW STRIPE/BORDER)</span>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-primary d-inline-flex align-items-center justify-content-center fw-bold shadow-sm px-3 py-1"
                                style={{ borderRadius: '8px' }}
                                onClick={() => setIsFilterModalVisible(true)}
                            >
                                <i className="ti ti-filter me-2"></i> Filter
                                {((selectedType ? 1 : 0) + (selectedStatus ? 1 : 0) + (selectedPoolTypeId ? 1 : 0)) > 0 && (
                                    <span
                                        className="ms-2 d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            backgroundColor: 'rgba(255,255,255,0.25)',
                                            color: '#fff',
                                            width: '20px',
                                            height: '20px',
                                            fontSize: '11px',
                                            fontWeight: 800
                                        }}
                                    >
                                        {((selectedType ? 1 : 0) + (selectedStatus ? 1 : 0) + (selectedPoolTypeId ? 1 : 0))}
                                    </span>
                                )}
                            </button>
                            <button
                                className="btn btn-light border d-inline-flex align-items-center justify-content-center fw-bold shadow-sm px-3 py-1"
                                style={{ borderRadius: '8px' }}
                                onClick={() => {
                                    setSelectedType("");
                                    setSelectedStatus("");
                                    setSelectedPoolTypeId("");
                                    fetchTowers("", "", "");
                                }}
                            >
                                <i className="ti ti-refresh me-2"></i> Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tower Visualization V3 */}
                {isLoading ? (
                    <div className="d-flex align-items-center justify-content-center py-5" style={{ minHeight: '400px' }}>
                        <Spin size="large" tip="Building Architectural View..." />
                    </div>
                ) : towers.length === 0 ? (
                    <Card bordered={false} className="text-center py-5 shadow-soft" style={{ borderRadius: '12px' }}>
                        <Empty description="No units found for this location." />
                    </Card>
                ) : (
                    <div className="row g-4">
                        {(() => {
                            const allFlats = towers.flatMap(tower =>
                                tower.floors.flatMap((f: any) =>
                                    f.flats.map((flat: any) => ({ ...flat, towerName: tower.name.replace("Tower ", "") }))
                                )
                            );
                            const totalOccupied = allFlats.filter((fl: any) => fl.status === 'Occupied').length;
                            const totalVacant = allFlats.filter((fl: any) => fl.status === 'Vacant').length;
                            const totalInitiated = allFlats.filter((fl: any) => fl.status === 'Vacant Initiated' || fl.status === 'Initiated').length;
                            const totalFlats = allFlats.length;

                            return (
                                <div className="col-12">
                                    <div className="card h-100 border-0 shadow-soft tower-premium-card" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                        <div className="card-header border-0 py-3 bg-white d-flex align-items-center justify-content-between px-4">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="ti ti-building-skyscraper text-danger" style={{ fontSize: '22px' }}></i>
                                                <h4 className="mb-0 fw-bold" style={{ color: '#0f172a', fontSize: '18px', letterSpacing: '0.5px' }}>All Units</h4>
                                                <span className="badge bg-light text-dark border ms-2" style={{ borderRadius: '12px', fontSize: '11px' }}>
                                                    {totalFlats} Units
                                                </span>
                                            </div>
                                        </div>

                                        <div className="card-body p-4 bg-light-soft">
                                            <div className="p-4 rounded-3 bg-white shadow-sm border border-light">
                                                {towers.map((tower, idx) => {
                                                    const blockLetter = tower.blockLetter || tower.name.replace("Tower ", "").trim();
                                                    const displayBlockName = tower.displayBlockName || (blockLetter.length === 1 ? `Block ${blockLetter}` : tower.name);
                                                    const blockTotalCount = tower.totalFlatsCount || (tower.quarterGroups ? tower.quarterGroups.reduce((a: number, b: any) => a + b.flats.length, 0) : tower.floors.flatMap((f: any) => f.flats).length);
                                                    const quarterGroups = tower.quarterGroups || [];

                                                    return (
                                                        <div key={idx} className={`${idx !== 0 ? 'mt-4 pt-4 border-top border-light' : ''}`}>
                                                            {/* Block Header with Line Separator */}
                                                            <div className="d-flex align-items-center gap-3 mb-3">
                                                                <span className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                                                                    <i className="ti ti-building text-primary" style={{ fontSize: '19px' }}></i>
                                                                    {displayBlockName}
                                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold" style={{ fontSize: '11px', borderRadius: '10px', padding: '3px 9px' }}>
                                                                        {blockTotalCount} units
                                                                    </span>
                                                                </span>
                                                                <div className="flex-grow-1" style={{ height: '1.5px', backgroundColor: '#e2e8f0' }}></div>
                                                            </div>

                                                            {/* Quarter Type Subsections */}
                                                            <div className="ps-1">
                                                                {quarterGroups.map((qGroup: any, qIdx: number) => (
                                                                    <div key={qIdx} className={`quarter-subsection ${qIdx !== 0 ? 'mt-3 pt-2' : ''}`}>
                                                                        {/* Quarter Type Subheading */}
                                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                                            <span className="badge bg-light text-dark border fw-bold px-2.5 py-1 d-flex align-items-center gap-1.5" style={{ fontSize: '12px', borderRadius: '6px' }}>
                                                                                <i className="ti ti-home-check text-muted" style={{ fontSize: '13px' }}></i>
                                                                                {qGroup.quarterType}
                                                                            </span>
                                                                            <span className="text-muted small fw-medium">
                                                                                — {qGroup.flats.length} {qGroup.flats.length === 1 ? 'unit' : 'units'}
                                                                            </span>
                                                                        </div>

                                                                        {/* Units Grid */}
                                                                        <div className="d-flex gap-2 flex-wrap w-100 ps-1">
                                                                            {qGroup.flats.map((flat: any, flIdx: number) => {
                                                                                const isSelected = isFlatSelected(flat);
                                                                                const eligibility = checkFlatEligibility(flat);
                                                                                const isDisabled = !eligibility.isEligible && selectedFlats.length > 0 && !isSelected;

                                                                                const catStr = (flat?.itemData?.category || flat?.category || "").toString().trim().toUpperCase();
                                                                                const isSc = catStr.startsWith("SC-") || catStr === "SC";
                                                                                const isSt = catStr.startsWith("ST-") || catStr === "ST";
                                                                                const isScOrSt = isSc || isSt;

                                                                                return (
                                                                                    <Popover
                                                                                        key={flIdx}
                                                                                        placement="top"
                                                                                        content={renderPopoverContent(flat)}
                                                                                        trigger={['hover', 'click']}
                                                                                        color="#0f172a"
                                                                                        overlayInnerStyle={{ borderRadius: '12px', padding: '12px' }}
                                                                                    >
                                                                                        <div
                                                                                            className={`premium-unit-box d-flex align-items-center justify-content-center fw-bold position-relative ${isSelected ? 'unit-selected' : ''}`}
                                                                                            onClick={(e) => {
                                                                                                if (flat.status === "Vacant") {
                                                                                                    handleToggleSelectFlat(flat, e);
                                                                                                }
                                                                                            }}
                                                                                            style={{
                                                                                                width: "56px",
                                                                                                height: "42px",
                                                                                                borderRadius: '8px',
                                                                                                cursor: isDisabled ? "not-allowed" : "pointer",
                                                                                                fontSize: "12px",
                                                                                                backgroundColor: isSelected ? '#38bdf8' : getStatusColor(flat.status),
                                                                                                color: '#fff',
                                                                                                borderTop: isScOrSt ? '4px solid #eab308' : 'none',
                                                                                                boxShadow: isSelected
                                                                                                    ? '0 0 0 3px #38bdf8, 0 6px 12px rgba(56, 189, 248, 0.4)'
                                                                                                    : isScOrSt
                                                                                                        ? `0 0 0 2px #eab308, 0 4px 6px -1px ${getStatusColor(flat.status)}30`
                                                                                                        : `0 4px 6px -1px ${getStatusColor(flat.status)}30`,
                                                                                                opacity: isDisabled ? 0.4 : 1,
                                                                                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                                                                                            }}
                                                                                            title={isDisabled ? eligibility.reason : undefined}
                                                                                        >
                                                                                            {isScOrSt && (
                                                                                                <div
                                                                                                    className="position-absolute d-flex align-items-center justify-content-center text-dark"
                                                                                                    style={{
                                                                                                        top: '2px',
                                                                                                        left: '2px',
                                                                                                        backgroundColor: '#eab308',
                                                                                                        color: '#000',
                                                                                                        fontSize: '7.5px',
                                                                                                        fontWeight: 900,
                                                                                                        padding: '0 2.5px',
                                                                                                        borderRadius: '2px',
                                                                                                        lineHeight: '9.5px',
                                                                                                        zIndex: 1,
                                                                                                        letterSpacing: '0.3px',
                                                                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.4)'
                                                                                                    }}
                                                                                                >
                                                                                                    {isSc ? 'SC' : 'ST'}
                                                                                                </div>
                                                                                            )}
                                                                                            {isSelected && (
                                                                                                <div
                                                                                                    className="position-absolute d-flex align-items-center justify-content-center bg-white text-primary rounded-circle"
                                                                                                    style={{
                                                                                                        width: '16px',
                                                                                                        height: '16px',
                                                                                                        top: '-4px',
                                                                                                        right: '-4px',
                                                                                                        fontSize: '10px',
                                                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                                                        zIndex: 2
                                                                                                    }}
                                                                                                >
                                                                                                    <i className="ti ti-check" style={{ fontSize: '11px', fontWeight: 900 }}></i>
                                                                                                </div>
                                                                                            )}
                                                                                            {blockLetter}-{padFlatNumber(flat.flatNo)}
                                                                                        </div>
                                                                                    </Popover>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="card-footer bg-white border-top-0 p-3 px-4">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div className="d-flex gap-4 align-items-center flex-wrap">
                                                    <Link
                                                        to={`${routes.staffQuarter.inventory.master}?location=${locationCode}&status=Occupied`}
                                                        className="d-flex align-items-center gap-2 text-decoration-none hover-opacity"
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="dot bg-success"></div>
                                                        <span className="text-muted small fw-medium">{totalOccupied} Occupied</span>
                                                    </Link>
                                                    <Link
                                                        to={`${routes.staffQuarter.inventory.master}?location=${locationCode}&status=Vacant`}
                                                        className="d-flex align-items-center gap-2 text-decoration-none hover-opacity"
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="dot bg-danger"></div>
                                                        <span className="text-muted small fw-medium">{totalVacant} Vacant</span>
                                                    </Link>
                                                    {totalInitiated > 0 && (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="dot bg-primary"></div>
                                                            <span className="text-muted small fw-medium">{totalInitiated} Initiated</span>
                                                        </div>
                                                    )}
                                                    <div className="d-flex align-items-center gap-2 border-start ps-3 ms-1">
                                                         <div style={{ width: '22px', height: '12px', background: '#28a745', borderTop: '3px solid #eab308', borderRadius: '2px', position: 'relative', boxShadow: '0 0 0 1px #eab308' }}>
                                                             <span style={{ position: 'absolute', top: '0px', left: '1px', background: '#eab308', color: '#000', fontSize: '4.5px', fontWeight: 900, padding: '0 1px', borderRadius: '1px', lineHeight: '6px' }}>SC/ST</span>
                                                         </div>
                                                         <span className="text-muted small fw-medium">SC/ST Category (Yellow Stripe/Border)</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>

            <Modal
                title={
                    <div className="d-flex align-items-center gap-2">
                        <i className="ti ti-checklist text-primary" style={{ fontSize: '20px' }}></i>
                        <span>Generate Willingness {selectedFlats.length > 1 ? `(${selectedFlats.length} Flats)` : `(Unit ${padFlatNumber(selectedFlat?.flatNo) || ''})`}</span>
                    </div>
                }
                open={isWillingnessModalVisible}
                onCancel={() => {
                    setIsWillingnessModalVisible(false);
                    setSkipRemark("");
                    setSkipRemarkError("");
                }}
                width={850}
                zIndex={1050}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setIsWillingnessModalVisible(false);
                        setSkipRemark("");
                        setSkipRemarkError("");
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="generate"
                        type="primary"
                        loading={isGenerating}
                        disabled={isGenerating || selectedRowKeys.length === 0}
                        onClick={handleGenerateWillingness}
                        style={{ backgroundColor: '#5b21b6', borderColor: '#5b21b6' }}
                    >
                        Generate Willingness ({selectedFlats.length > 0 ? selectedFlats.length : 1} Flat{selectedFlats.length > 1 ? 's' : ''})
                    </Button>
                ]}
            >
                {/* Batch Confirmation Details */}
                <div className="mb-3 p-3 bg-light rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                        <div className="fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>
                            Selected Flat(s):
                            <span className="text-primary ms-2 font-monospace">
                                {(selectedFlats.length > 0 ? selectedFlats : [selectedFlat]).map((f: any) => `${f?.itemData?.block || f?.blockLetter || ''}-${padFlatNumber(f?.flatNo)}`).join(', ')}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-3 text-muted small">
                            <span>Quarter Type: <strong className="text-dark">{allowedQuarterType || getFlatQuarterType(selectedFlat)}</strong></span>
                            <span>Pool: <strong className="text-dark">{allowedPool || getFlatPool(selectedFlat)}</strong></span>
                        </div>
                    </div>
                    <Tag color="purple" style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '12px' }}>
                        {(selectedFlats.length > 0 ? selectedFlats.length : 1)} Flat(s) Selected
                    </Tag>
                </div>

                <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <Input
                            placeholder="Search by Emp No or Name..."
                            prefix={<i className="ti ti-search text-muted me-1" />}
                            value={empNoSearch}
                            onChange={(e) => setEmpNoSearch(e.target.value)}
                            allowClear
                            style={{ width: '230px', borderRadius: '6px' }}
                        />
                        {empNoSearch.trim() && (
                            <span className="text-muted small ms-1">
                                ({filteredPriorityList.length} candidate{filteredPriorityList.length !== 1 ? 's' : ''})
                            </span>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">Requests per flat:</span>
                            <input
                                type="number"
                                min="1"
                                className="form-control form-control-sm"
                                style={{ width: '70px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                                value={customPageSize}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val > 0) setCustomPageSize(val);
                                    else if (e.target.value === "") setCustomPageSize(0 as any); // allow clearing to type a new number
                                }}
                                onBlur={() => {
                                    if (!customPageSize || customPageSize < 1) setCustomPageSize(10);
                                }}
                            />
                            <button
                                className="btn btn-sm btn-primary py-1 px-2"
                                style={{ backgroundColor: '#5b21b6', borderColor: '#5b21b6', color: 'white' }}
                                onClick={() => fetchPriorityList(selectedFlat, customPageSize || 10)}
                            >
                                Apply
                            </button>
                        </div>
                        <Tag color="purple">{selectedRowKeys.length} Selected</Tag>
                    </div>
                </div>
                <Table
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
                    }}
                    columns={priorityColumns}
                    dataSource={filteredPriorityList}
                    loading={isPriorityLoading}
                    pagination={{
                        pageSize: customPageSize || 10,
                        showSizeChanger: false,
                        position: ['topRight', 'bottomRight'],
                        showTotal: (total, range) => `Showing ${range[0]} - ${range[1]} of ${total} entries`
                    }}
                    size="small"
                    scroll={{ y: 400 }}
                />

                {/* Mandatory Remark Textarea when skipping higher-priority employees */}
                {isSkippingHigherPriority && (
                    <div className="mt-3 p-3 rounded-3" style={{ backgroundColor: '#fff8f6', border: '1px solid #fecdd3' }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="ti ti-alert-triangle text-danger" style={{ fontSize: '18px' }} />
                            <label className="form-label mb-0 fw-bold text-dark" style={{ fontSize: '13.5px' }}>
                                Reason for skipping higher-priority employee(s) <span className="text-danger">*</span>
                            </label>
                        </div>
                        {skippedEmployees.length > 0 && (
                            <div className="text-muted small mb-2" style={{ fontSize: '12px' }}>
                                Higher-priority employee(s) skipped:{" "}
                                <strong className="text-danger">
                                    {skippedEmployees.map((e: any) => `${e.priority || `#${e.priorityNum}`} (${e.employeeName || e.name || e.employeeNumber || e.empNo || 'Employee'})`).join(', ')}
                                </strong>
                            </div>
                        )}
                        <Input.TextArea
                            rows={3}
                            placeholder="Please enter the reason for skipping higher-priority employee(s)..."
                            value={skipRemark}
                            onChange={(e) => {
                                setSkipRemark(e.target.value);
                                if (e.target.value.trim()) {
                                    setSkipRemarkError("");
                                }
                            }}
                            status={skipRemarkError ? "error" : undefined}
                            style={{ borderRadius: '6px' }}
                        />
                        {skipRemarkError && (
                            <div className="text-danger small mt-1 d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                <i className="ti ti-alert-circle" /> {skipRemarkError}
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                title={
                    <div className="d-flex align-items-center gap-2 pb-2 mb-1">
                        <i className="ti ti-filter text-primary" style={{ fontSize: '22px' }}></i>
                        <span className="fw-bold" style={{ fontSize: '18px', color: '#0f172a' }}>Filter Inventory</span>
                    </div>
                }
                open={isFilterModalVisible}
                onCancel={() => setIsFilterModalVisible(false)}
                centered
                width={400}
                closeIcon={<i className="ti ti-x text-muted" style={{ fontSize: '20px' }}></i>}
                footer={
                    <div className="d-flex justify-content-end gap-2 pt-3 mt-3 border-top">
                        <button
                            className="btn btn-light border fw-bold px-3"
                            style={{ borderRadius: '8px', height: '36px' }}
                            onClick={() => {
                                setSelectedType("");
                                setSelectedStatus("");
                                setSelectedPoolTypeId("");
                                fetchTowers("", "", "");
                                setIsFilterModalVisible(false);
                            }}
                        >
                            Reset
                        </button>
                        <button
                            className="btn btn-primary fw-bold px-3 shadow-sm"
                            style={{ borderRadius: '8px', height: '36px' }}
                            onClick={() => {
                                fetchTowers();
                                setIsFilterModalVisible(false);
                            }}
                        >
                            Apply Filters
                        </button>
                    </div>
                }
            >
                <div className="row g-4 mt-2 px-1">
                    <div className="col-12">
                        <label className="form-label text-muted mb-2 fw-bold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>QUARTER TYPE</label>
                        <Select
                            className="w-100 premium-select"
                            showSearch
                            allowClear
                            optionFilterProp="children"
                            value={selectedType || undefined}
                            onChange={(val) => setSelectedType(val || "")}
                            options={quarterTypes.map((t: any) => ({ value: t.name, label: t.name }))}
                            placeholder="Select Quarter Type"
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label text-muted mb-2 fw-bold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>POOL TYPE</label>
                        <Select
                            className="w-100 premium-select"
                            showSearch
                            allowClear
                            optionFilterProp="children"
                            value={selectedPoolTypeId || undefined}
                            onChange={(val) => setSelectedPoolTypeId(val || "")}
                            options={poolTypes.map((t: any) => ({ value: t.id, label: t.namePool }))}
                            placeholder="Select Pool Type"
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label text-muted mb-2 fw-bold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>STATUS</label>
                        <Select
                            className="w-100 premium-select"
                            allowClear
                            value={selectedStatus || undefined}
                            onChange={(val) => setSelectedStatus(val || "")}
                            options={[
                                { value: 'VACANT', label: 'Vacant' },
                                { value: 'OCCUPIED', label: 'Occupied' },
                                { value: 'VACANT_INITIATED', label: 'Vacant Initiated' }
                            ]}
                            placeholder="Select Status"
                        />
                    </div>
                </div>
            </Modal>

            {/* Floating Multi-Select Action Bar */}
            {selectedFlats.length > 0 && !isWillingnessModalVisible && (
                <div
                    className="position-fixed bottom-0 start-50 translate-middle-x mb-4 shadow-lg bg-dark text-white rounded-4 p-3 d-flex align-items-center justify-content-between gap-4 border border-secondary"
                    style={{ zIndex: 1040, minWidth: '420px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.3)' }}
                >
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <span className="badge bg-primary fs-6 px-3 py-2" style={{ borderRadius: '20px' }}>
                            {selectedFlats.length} Flat{selectedFlats.length > 1 ? 's' : ''} Selected
                        </span>
                        <div className="d-flex align-items-center gap-2 text-slate-300 small">
                            <span className="fw-semibold text-white">Flats:</span>
                            <span className="text-info font-monospace fw-bold">
                                {selectedFlats.map(f => `${f.itemData?.block || f.blockLetter || ''}-${padFlatNumber(f.flatNo)}`).join(', ')}
                            </span>
                        </div>
                        {allowedQuarterType && (
                            <span className="badge bg-secondary text-light">Type: {allowedQuarterType}</span>
                        )}
                        {allowedPool && (
                            <span className="badge bg-info text-dark">Pool: {allowedPool}</span>
                        )}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-outline-light btn-sm px-3"
                            onClick={() => setSelectedFlats([])}
                        >
                            Clear
                        </button>
                        <button
                            className="btn btn-primary btn-sm px-4 fw-bold shadow-sm"
                            style={{ backgroundColor: '#5b21b6', borderColor: '#5b21b6' }}
                            onClick={handleOpenMultiWillingnessModal}
                        >
                            <i className="ti ti-checklist me-1"></i> Generate Willingness ({selectedFlats.length})
                        </button>
                    </div>
                </div>
            )}

            {showEditModal && selectedFlatRecord && (
                <EditFlatModal
                    show={showEditModal}
                    onHide={() => {
                        setShowEditModal(false);
                        setSelectedFlatRecord(null);
                    }}
                    flat={selectedFlatRecord}
                    onUpdate={handleUpdateFlatPool}
                />
            )}

            <style>{`
                .shadow-soft {
                    box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.05), 0 2px 10px -3px rgba(0, 0, 0, 0.02) !important;
                    border: 1px solid #f1f5f9 !important;
                }
                .bg-light-soft {
                    background-color: #f8fafc;
                }
                .premium-unit-box:hover {
                    transform: translateY(-4px) scale(1.1);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
                    z-index: 10;
                }
                .tower-premium-card {
                    transition: all 0.3s ease;
                    border: 1px solid #f1f5f9 !important;
                }
                .tower-premium-card:hover {
                    border-color: #e2e8f0 !important;
                    box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.08) !important;
                }
                .floor-tag {
                    padding-right: 20px;
                    border-right: 2px solid #e2e8f0;
                }
                .dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }
                .premium-tooltip .tooltip-inner {
                    background-color: #0f172a;
                    border-radius: 12px;
                    padding: 12px;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
                    border: 1px solid #1e293b;
                }
                .premium-tooltip .tooltip-arrow::before {
                    border-top-color: #0f172a;
                }
                .premium-select .ant-select-selector {
                    border-radius: 8px !important;
                    border-color: #e2e8f0 !important;
                    padding-left: 12px !important;
                    font-weight: 500;
                    color: #1e293b;
                }
                .premium-select:hover .ant-select-selector,
                .premium-select.ant-select-focused .ant-select-selector {
                    border-color: #0d6efd !important;
                    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1) !important;
                }
                .hover-opacity {
                    transition: opacity 0.2s ease-in-out;
                }
                .hover-opacity:hover {
                    opacity: 0.7;
                    text-decoration: underline !important;
                }
            `}</style>
        </div>
    );
};

export default LocationFlatView;
